import { pxToEm } from 'util/pxToEm';
import { Box } from '@mui/material';
import { WarningRounded } from '@mui/icons-material';
import React, { forwardRef, ReactElement } from 'react';
import { useUID } from 'react-uid';
import { Input, Label } from 'components/sx';
import clsx from 'clsx';

type InputProps = JSX.IntrinsicElements['input'];

export interface TextInputProps extends Omit<InputProps, 'ref'> {
  name?: string;
  label?: string;
  hasError?: boolean;
  hideWarningIcon?: boolean;
  className?: string;
  inputClassName?: string;
  children?: React.ReactNode;
}

export const TextInput = forwardRef(
  (
    {
      name,
      label,
      hasError = false,
      hideWarningIcon = false,
      children,
      className,
      inputClassName,
      ...inputProps
    }: TextInputProps,
    ref?: React.Ref<HTMLInputElement>,
  ): ReactElement => {
    const id = name ? `${name}-${useUID()}` : useUID();

    return (
      <>
        <Box
          display="flex"
          flexDirection="column"
          className={clsx('TextInput', className)}
        >
          {label && (
            <Label
              htmlFor={id}
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: theme =>
                  hasError
                    ? theme.palette.error.main
                    : theme.palette.text.primary,
                cursor: 'pointer',
                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                whiteSpace: 'nowrap',
                userSelect: 'none',
              }}
            >
              {label}
            </Label>
          )}
          <Box position="relative">
            {hasError && !hideWarningIcon && (
              <Box position="absolute" bottom={10} right={10} lineHeight={0}>
                <WarningRounded
                  sx={{ color: theme => theme.palette.error.main }}
                />
              </Box>
            )}
            <Input
              name={name}
              id={id}
              ref={ref}
              aria-invalid={hasError}
              className={inputClassName}
              sx={{
                width: '100%',
                background: theme => theme.palette.neutral.main,
                border: '1px solid',
                borderRadius: 0,
                color: theme => theme.palette.text.primary,
                outline: 'none',
                fontFamily: 'Poppins',
                fontSize: pxToEm(14),
                borderColor: theme =>
                  hasError
                    ? theme.palette.error.light
                    : theme.palette.neutral.dark,
                padding: '0 10px',
                height: 50,
                paddingRight: hasError ? 47 : 10,
                '&::placeholder': {
                  color: '#A7A7A7',
                },
                '&:focus': {
                  borderColor: theme => theme.palette.primary.main,
                  boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}`,
                },
                '&:focus + *': {
                  borderColor: theme => theme.palette.primary.main,
                  borderWidth: 2,
                },
                '.PasswordInput &': {
                  paddingLeft: 'calc(1em + 30px + 2px + 10px)',
                },
              }}
              {...inputProps}
            />
            {children}
          </Box>
        </Box>
      </>
    );
  },
);
